import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  updateDoc,
} from 'firebase/firestore';

import UserNav from '../../components/navbar/user-nav';
import AdminSidebar from '../../components/admin-sidebar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';

const AMENIDADES = ['Parque', 'Balcón', 'Área verde', 'Wi-Fi', 'Estacionamiento', 'Jacuzzi'];
const ESTATUS_OPTIONS = ['Disponible', 'Pendiente', 'Vendida'];
const TIPO_OPTIONS = ['bodega', 'casa', 'departamento', 'edificio', 'local', 'oficina', 'terreno'];
const ESTADO_OPTIONS = ['venta', 'renta'];

export default function ViewProperty() {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [propiedad, setPropiedad] = useState<any>(null);
  const [formData, setFormData] = useState<any>(null);
  const [show, setShow] = useState(false);
  const [editable, setEditable] = useState(false);
  const [afiliados, setAfiliados] = useState<any[]>([]);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (fbUser) => {
      if (!fbUser?.email) return;

      let snap = await getDocs(query(collection(db, 'asociado'), where('correo', '==', fbUser.email)));
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const id = snap.docs[0].id;
        const inmobiliaria_id = data.inmobiliaria_id;

        let nombreInmo = '';
        if (inmobiliaria_id) {
          const inmoSnap = await getDocs(query(collection(db, 'inmobiliaria'), where('inmobiliaria_id', '==', inmobiliaria_id)));
          if (!inmoSnap.empty) {
            nombreInmo = inmoSnap.docs[0].data().nombre || '';
          }
        }

        setUser({ ...data, tipo: 'asociado', id, inmobiliaria_id, inmobiliaria_nombre: nombreInmo });
        return;
      }

      snap = await getDocs(query(collection(db, 'asesor'), where('correo', '==', fbUser.email)));
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const id = snap.docs[0].id;
        const inmobiliaria_id = Array.isArray(data.inmobiliaria_id) ? data.inmobiliaria_id[0] : data.inmobiliaria_id;

        let nombreInmo = '';
        if (inmobiliaria_id) {
          const inmoSnap = await getDocs(query(collection(db, 'inmobiliaria'), where('inmobiliaria_id', '==', inmobiliaria_id)));
          if (!inmoSnap.empty) {
            nombreInmo = inmoSnap.docs[0].data().nombre || '';
          }
        }

        setUser({ ...data, tipo: 'asesor', id, inmobiliaria_id, inmobiliaria_nombre: nombreInmo, asesor_id: data.asesor_id });
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) return;
      try {
        const docRef = doc(db, 'propiedades', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const propertyData = { id: snap.id, ...snap.data() };
          setPropiedad(propertyData);
          setFormData({ ...propertyData });

          if ('asesor_id' in propertyData && Array.isArray(propertyData.asesor_id)) {
            const q = query(collection(db, 'asesor'), where('asesor_id', 'in', propertyData.asesor_id));
            const afSnap = await getDocs(q);
            const filtered = afSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setAfiliados(filtered);
          }
        }
      } catch (err) {
        console.error('Error al cargar propiedad:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [id]);

  const puedeEditar = user && (
    (user.tipo === 'asesor' && Array.isArray(propiedad?.asesor_id) && propiedad.asesor_id.includes(user.asesor_id)) ||
    (user.tipo === 'asociado' && (
      (Array.isArray(propiedad?.inmobiliaria_id) && propiedad.inmobiliaria_id.includes(user.inmobiliaria_id)) ||
      propiedad?.inmobiliaria_id === user.inmobiliaria_id
    ))
  );

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (type === 'checkbox') {
      setFormData((prev: any) => {
        const current = new Set(prev.amenidades || []);
        if (checked) current.add(value);
        else current.delete(value);
        return { ...prev, amenidades: Array.from(current) };
      });
    } else {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, 'propiedades', id!), formData);
      alert('Cambios guardados.');
      setEditable(false);
    } catch (err) {
      console.error('Error al guardar:', err);
    }
  };

  if (loading || !propiedad) return <p className="text-center mt-5">Cargando propiedad...</p>;

  return (
    <>
      <UserNav nombreUsuario={user?.nombre} nombreInmobiliaria={user?.inmobiliaria_nombre} />
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-3 col-md-12">
            <AdminSidebar show={show} setShow={setShow} nombre={user?.nombre} inmobiliaria={user?.inmobiliaria_nombre} />
          </div>

          <div className="col-lg-9 col-md-12 my-5">
            {puedeEditar ? (
              <form onSubmit={handleSubmit}>
                <div className="d-flex justify-content-between align-items-center">
                  <h2 className="mb-4">Información de la Propiedad</h2>
                  {!editable && <button className="btn btn-warning" onClick={(e) => { e.preventDefault(); setEditable(true); }}>Editar</button>}
                </div>

                <div className="form-group">
                  <label>Estatus</label>
                  <select name="estatus" value={formData.estatus || ''} onChange={handleChange} className="form-control" disabled={!editable}>
                    <option value="">Selecciona estatus</option>
                    {ESTATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </div>

                {editable && (
                  <>
                    <div className="form-group">
                      <label>Nombre</label>
                      <input name="nombre" value={formData.nombre} onChange={handleChange} className="form-control" required />
                    </div>
                    <div className="form-group">
                      <label>Precio</label>
                      <input name="precio" type="number" value={formData.precio} onChange={handleChange} className="form-control" required />
                    </div>
                    <div className="form-group">
                      <label>Recámaras</label>
                      <input name="recamaras" type="number" value={formData.recamaras || ''} onChange={handleChange} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>Baños</label>
                      <input name="baños" type="number" value={formData.baños || ''} onChange={handleChange} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>Estacionamiento</label>
                      <input name="estacionamiento" type="number" value={formData.estacionamiento || ''} onChange={handleChange} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>Terreno (m²)</label>
                      <input name="terreno_m2" type="number" value={formData.terreno_m2 || ''} onChange={handleChange} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>Construcción (m²)</label>
                      <input name="construccion_m2" type="number" value={formData.construccion_m2 || ''} onChange={handleChange} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>Dirección (URL)</label>
                      <input name="direccion" value={formData.direccion || ''} onChange={handleChange} className="form-control" />
                    </div>
                    <div className="form-group">
                      <label>Tipo</label>
                      <select name="tipo" value={formData.tipo || ''} onChange={handleChange} className="form-control">
                        <option value="">Selecciona tipo</option>
                        {TIPO_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Estado</label>
                      <select name="estado" value={formData.estado || ''} onChange={handleChange} className="form-control">
                        <option value="">Selecciona estado</option>
                        {ESTADO_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Descripción</label>
                      <textarea name="descripcion" value={formData.descripcion || ''} onChange={handleChange} className="form-control"></textarea>
                    </div>
                    <div className="form-group">
                      <label>Amenidades</label>
                      <div className="d-flex flex-wrap gap-3">
                        {AMENIDADES.map((a) => (
                          <label key={a} className="form-check-label">
                            <input
                              type="checkbox"
                              name="amenidades"
                              value={a}
                              checked={formData.amenidades?.includes(a)}
                              onChange={handleChange}
                              className="form-check-input me-1"
                            />
                            {a}
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {user?.tipo === 'asociado' && afiliados.length > 0 && (
                  <div className="form-group col-md-12 mt-4">
                    <label>Afiliados asignados a esta propiedad:</label>
                    <ul className="list-group">
                      {afiliados.map((a, i) => (
                        <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                          {a.nombre || a.correo}
                          <Link to={`/agent-page/${a.id}`} className="btn btn-sm btn-outline-primary">Ver perfil</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {editable && (
                  <div className="form-group mt-3">
                    <button type="submit" className="btn btn-success">Guardar Cambios</button>
                  </div>
                )}
              </form>
            ) : (
              <div className="alert alert-warning">
                <h5>No tienes permiso para editar esta propiedad.</h5>
                <p>Verifica que seas el asesor asignado o que pertenezcas a la inmobiliaria propietaria.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
